import { FC, useState } from 'react'
import { Link } from 'react-router-dom'
import {IProductDraftModel} from '../cors/_models'
import { useIntl } from 'react-intl'
import SharedData from '../../common/SharedData'
import CustomModal from '../../common/CustomModal'
import {Button} from 'react-bootstrap'
import {deleteDraftProduct} from '../cors/_requests'

type IProps = {
  product: IProductDraftModel
  onDeleted: (requestId: number) => void
  showAlert: (alert: {title: string; message: string; status: boolean}) => void
}

const DraftTableRow: FC<IProps> = ({product, onDeleted, showAlert}) => {
  const { formatMessage } = useIntl()
  const {currentUser, language} = SharedData()
  const [isLoading, setIsLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleDelete = async () => {
    setIsLoading(true)

    try {
      const response = await deleteDraftProduct(product.request_id, currentUser?.token, language)
      setIsModalOpen(false)
      onDeleted(product.request_id)
      showAlert({
        title: formatMessage({id: 'draft_deleted'}),
        message: response.data.message,
        status: true,
      })
    } catch (error: any) {
      // 404 = no draft with this id for this supplier any more (already
      // deleted in another tab, or no longer a draft), so drop the stale
      // row instead of leaving one that can never be deleted.
      if (error?.response?.status === 404) {
        setIsModalOpen(false)
        onDeleted(product.request_id)
      }
      showAlert({
        title: formatMessage({id: 'draft_delete_error'}),
        message:
          error?.response?.data?.message || formatMessage({id: 'draft_delete_failed'}),
        status: false,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <tr>
      <td className="w-250px" style={{ padding: '10px', verticalAlign: 'middle' }}>
        {product.name}
      </td>

      <td
        style={{
          whiteSpace: 'nowrap',
          padding: '10px',
          paddingRight: '15px',
          verticalAlign: 'middle',
        }}
      >
        {product.barcode}
      </td>

      <td className="text-nowrap" style={{ padding: '5px', verticalAlign: 'middle' }}>
        {product.model}
      </td>

      <td className="text-nowrap" style={{ padding: '5px', verticalAlign: 'middle' }}>
        {isLoading ? (
          <span className="align-middle spinner-border spinner-border-sm"></span>
        ) : (
          <div className='d-flex flex-wrap gap-2'>
            <Link
              className="btn btn-secondary p-2 px-5 align-self-baseline"
              style={{ textAlign: 'center', width: '100px'}}
              // Drafts are new-product (add) drafts: open them in the
              // edit-product page as request_type='add' via the &draft suffix.
              to={`/products/product-edit/${product.request_id}&draft`}
            >
              {formatMessage({ id: 'edit' })}
            </Link>
            <button
              type='button'
              className='btn btn-danger p-2 px-5 align-self-baseline'
              style={{textAlign: 'center', width: '100px'}}
              onClick={() => setIsModalOpen(true)}
            >
              {formatMessage({id: 'delete'})}
            </button>
          </div>
        )}
        <CustomModal
          eventSlot={
            <Button variant='danger' onClick={handleDelete} disabled={isLoading}>
              {isLoading && <span className='spinner-border spinner-border-sm me-2' />}
              {formatMessage({id: 'delete'})}
            </Button>
          }
          title={formatMessage({id: 'delete_draft'})}
          show={isModalOpen}
          onCloseModal={() => !isLoading && setIsModalOpen(false)}
        >
          <p>{formatMessage({id: 'delete_draft_confirm'})}</p>
        </CustomModal>
      </td>
    </tr>
  )
}

export default DraftTableRow
